import { toast } from 'sonner'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { useDeleteSection } from '../hooks'
import type { FormSchema, FormSection } from '../types'

type Props = {
  schema: FormSchema
  selectedCode: string | null
  onSelect: (code: string) => void
  onAdd: () => void
  onEdit: (section: FormSection) => void
  objectType: string
}

export function SectionListPanel({ schema, selectedCode, onSelect, onAdd, onEdit, objectType }: Props) {
  const confirm = useConfirm()
  const del = useDeleteSection(objectType)

  const sections = [...schema.sections].sort((a, b) => a.sortOrder - b.sortOrder)

  return (
    <aside className="w-72 border-r bg-slate-50 flex flex-col">
      <div className="px-3 py-2 border-b flex items-center justify-between">
        <span className="text-xs uppercase text-slate-500">Sekcje</span>
        <button
          type="button"
          onClick={onAdd}
          className="text-xs px-2 py-1 border rounded bg-white hover:bg-slate-100"
        >
          + Dodaj
        </button>
      </div>
      <div className="flex-1 overflow-auto">
        {sections.length === 0 && (
          <p className="p-3 text-xs text-slate-500">Brak sekcji - dodaj pierwszą.</p>
        )}
        {sections.map(s => (
          <div
            key={s.code}
            className={[
              'flex items-center justify-between px-3 py-2 border-b cursor-pointer text-sm',
              selectedCode === s.code ? 'bg-white border-l-4 border-l-sky-600' : 'hover:bg-white',
            ].join(' ')}
            onClick={() => onSelect(s.code)}
          >
            <div className="flex flex-col min-w-0">
              <span className="truncate">{s.labelPl}</span>
              <span className="text-[10px] font-mono text-slate-500">{s.code}</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={e => { e.stopPropagation(); onEdit(s) }}
                className="text-xs text-slate-600 hover:text-slate-900 px-1"
                title="Edytuj"
              >✎</button>
              <button
                type="button"
                onClick={async e => {
                  e.stopPropagation()
                  const ok = await confirm({
                    title: `Usunąć sekcję ${s.code}?`,
                    description: 'Możliwe tylko gdy sekcja nie ma aktywnych pól.',
                    confirmLabel: 'Usuń',
                    variant: 'destructive',
                  })
                  if (ok) del.mutate(s.code, { onError: err => toast.error(formatError(err)) })
                }}
                className="text-xs text-red-600 hover:text-red-800 px-1"
                title="Usuń"
              >×</button>
            </div>
          </div>
        ))}
      </div>
    </aside>
  )
}

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd usuwania'
}
